import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Plus, Calendar, Crown, TrendingUp } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { Card, Button } from '../../../components/ui'
import CreateVoterModal from '../../../components/CreateVoterModal'

interface DayRow { date: string; count: number }
interface LeaderRow { leader_name: string | null; count: number }
interface DashData {
  total: number
  days: number
  period_total: number
  by_day: DayRow[]
  by_leader: LeaderRow[]
}

const PERIODS = [30, 60, 90] as const

function formatDay(iso: string): string {
  // iso = YYYY-MM-DD
  const [y, m, d] = iso.split('-')
  const date = new Date(Number(y), Number(m) - 1, Number(d))
  const weekday = date.toLocaleDateString('pt-BR', { weekday: 'short' })
  return `${d}/${m} · ${weekday}`
}

export default function DigitadorDashboardPage() {
  const navigate = useNavigate()
  const [days, setDays] = useState<number>(30)
  const [data, setData] = useState<DashData | null>(null)
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)

  const load = useCallback((d: number) => {
    setLoading(true)
    axios.get(buildApiUrl('/v1/digitador/dashboard'), { params: { days: d } })
      .then(res => setData(res.data.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load(days) }, [days, load])

  const maxCount = Math.max(1, ...(data?.by_day.map(r => r.count) || [1]))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meu painel</h1>
          <p className="text-gray-500 mt-1">Contatos que você cadastrou</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => setAddOpen(true)}>Novo contato</Button>
      </div>

      {/* Cards de total */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-500 flex items-center justify-center flex-shrink-0"><Users className="w-5 h-5" /></div>
            <div>
              <p className="text-sm text-gray-500">Total cadastrado</p>
              <p className="text-2xl font-bold text-gray-900">{(data?.total ?? 0).toLocaleString('pt-BR')}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center flex-shrink-0"><TrendingUp className="w-5 h-5" /></div>
            <div>
              <p className="text-sm text-gray-500">Nos últimos {days} dias</p>
              <p className="text-2xl font-bold text-gray-900">{(data?.period_total ?? 0).toLocaleString('pt-BR')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtro de período */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {PERIODS.map(p => (
          <button key={p} onClick={() => setDays(p)}
            className={clsx('px-4 py-2 rounded-md text-sm font-medium transition-colors', days === p ? 'bg-white text-brand-600 shadow-xs' : 'text-gray-500 hover:text-gray-700')}>
            Últimos {p} dias
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Dia a dia */}
        <Card padding="none" className="lg:col-span-2">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700">Cadastros por dia</h3>
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400">Carregando…</div>
          ) : !data || data.by_day.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">Nenhum cadastro no período.</div>
          ) : (
            <div className="divide-y divide-gray-50 max-h-[28rem] overflow-y-auto">
              {data.by_day.map(row => (
                <div key={row.date} className="flex items-center gap-4 px-5 py-3">
                  <span className="text-sm text-gray-700 w-28 flex-shrink-0">{formatDay(row.date)}</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-500 rounded-full" style={{ width: `${(row.count / maxCount) * 100}%` }} />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-12 text-right flex-shrink-0">{row.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Por liderança */}
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <Crown className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700">Por liderança</h3>
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400">Carregando…</div>
          ) : !data || data.by_leader.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">Sem dados no período.</div>
          ) : (
            <div className="divide-y divide-gray-50 max-h-[28rem] overflow-y-auto">
              {data.by_leader.map((row, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-3">
                  <span className="text-sm text-gray-700 truncate pr-3">{row.leader_name || 'Sem liderança'}</span>
                  <span className="text-sm font-semibold text-gray-900 flex-shrink-0">{row.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Após cadastrar, direciona para a tela de eleitores (onde ele vê só os que adicionou) */}
      <CreateVoterModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); navigate('/digitador/voters') }} type="contact" />
    </div>
  )
}
